import React from 'react';
import { Card, Form, DatePicker, Select, Space, Typography } from 'antd';
import {
  CalendarOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/es_ES';
import dayjs from 'dayjs';
import { brand } from '../../styles/theme';

const { Text } = Typography;
const { Option } = Select;

// Las cinco tarjetas del panel comparten cabecera y separación: icono en rojo
// primario y título en negro. Antes cada una llevaba su propio color y dos de
// ellas un borde de 2px que la maqueta ya no usa.
const cardStyle = { marginBottom: 16 };

const panelTitle = (Icon, label) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
    <Icon style={{ color: brand.primary, fontSize: 16 }} />
    {label}
  </span>
);

const ArticleSidePanel = ({
  sections,
  isEditing,
  currentArticle,
  form,
  imageUrl,
  audioUrl,
  hasVideo,
  videoUrl,
  videoThumbnail,
  content,
  detectVideoType
}) => {
  return (
    <>
      {/* Programar publicación */}
      <Card
        title={panelTitle(CalendarOutlined, 'Programar publicación')}
        size="small"
        style={cardStyle}
      >
        <Form.Item
          name="date"
          style={{ marginBottom: 0 }}
          tooltip="Selecciona cuándo se publicará este artículo. Déjalo vacío para usar la fecha actual."
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            placeholder="Seleccionar fecha y hora"
            style={{ width: '100%' }}
            locale={locale}
          />
        </Form.Item>
      </Card>

      {/* Sección */}
      <Card
        title={panelTitle(FolderOutlined, 'Sección')}
        size="small"
        style={cardStyle}
      >
        <Form.Item
          name="section"
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder="Selecciona una sección"
            showSearch
            allowClear
            optionFilterProp="children"
          >
            {sections.map(section => (
              <Option key={section.section_name} value={section.section_name}>
                {section.section_name} ({section.article_count} artículos)
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      {/* Información */}
      <Card
        title={panelTitle(InfoCircleOutlined, 'Información')}
        size="small"
        style={cardStyle}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Estado: </Text>
            <Text strong>{isEditing ? 'Editando' : 'Nuevo artículo'}</Text>
          </div>
          {isEditing && currentArticle && (
            <>
              <div>
                <Text type="secondary">ID: </Text>
                <Text strong>{currentArticle.id}</Text>
              </div>
              <div>
                <Text type="secondary">Creado: </Text>
                <Text strong>
                  {new Date(currentArticle.created_at).toLocaleDateString('es-ES')}
                </Text>
              </div>
              <div>
                <Text type="secondary">Actualizado: </Text>
                <Text strong>
                  {new Date(currentArticle.updated_at).toLocaleDateString('es-ES')}
                </Text>
              </div>
            </>
          )}
          <div>
            <Text type="secondary">Autor: </Text>
            <Text strong>{currentArticle?.author || 'Agustinos'}</Text>
          </div>
          <div>
            <Text type="secondary">Publicación: </Text>
            <Text strong>
              {form.getFieldValue('date') 
                ? `Programada: ${dayjs(form.getFieldValue('date')).format('DD/MM/YYYY HH:mm')}` 
                : 'Inmediata'
              }
            </Text>
          </div>
        </Space>
      </Card>

      {/* Contenido Multimedia */}
      <Card
        title={panelTitle(PictureOutlined, 'Contenido Multimedia')}
        size="small"
        style={cardStyle}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Imagen: </Text>
            <Text strong>{imageUrl ? 'Sí' : 'No'}</Text>
          </div>
          <div>
            <Text type="secondary">Audio: </Text>
            <Text strong>{audioUrl ? 'Sí' : 'No'}</Text>
          </div>
          <div>
            <Text type="secondary">Video: </Text>
            <Text strong>{hasVideo ? `Sí (${detectVideoType(videoUrl) || 'No detectado'})` : 'No'}</Text>
          </div>
          {hasVideo && videoUrl && (
            <div>
              <Text type="secondary">Thumbnail: </Text>
              <Text strong>{videoThumbnail ? 'Sí' : 'No'}</Text>
            </div>
          )}
        </Space>
      </Card>

      {/* Estadísticas */}
      <Card
        title={panelTitle(BarChartOutlined, 'Estadísticas')}
        size="small"
        style={cardStyle}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Caracteres: </Text>
            <Text strong>{content.replace(/<[^>]*>/g, '').length}</Text>
          </div>
          <div>
            <Text type="secondary">Palabras: </Text>
            <Text strong>
              {content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length}
            </Text>
          </div>
          <div>
            <Text type="secondary">Tiempo de lectura: </Text>
            <Text strong>
              {Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min
            </Text>
          </div>
          <div>
            <Text type="secondary">Imágenes en contenido: </Text>
            <Text strong>
              {(content.match(/<img[^>]+>/g) || []).length}
            </Text>
          </div>
          <div>
            <Text type="secondary">Enlaces: </Text>
            <Text strong>
              {(content.match(/<a[^>]+>/g) || []).length}
            </Text>
          </div>
        </Space>
      </Card>
    </>
  );
};

export default ArticleSidePanel;

